<template>
    <fragment>
        <app-variable ref="input" :icon="isCalculated ? 'calculator' : null"
            :model="model" :isCalculated="!!isCalculated" :currency="currency"
            variant="transparent wrapped-field"
            @focus.self="onFocusVariable" @input="onInputVariable"
        >
            <i v-if="isEstimable" ref="toggle" tabindex="-1" @click.self="onToggleMenu" class="large secondary question circle link icon"></i>
        </app-variable>
        <div v-if="isEstimable" ref="menu" class="ui inverted blue pointing compressed segment" 
            :class="{hidden: !menu.active}" @focusout="onToggleMenu" tabindex="-1">
            <div class="field">
                <div class="ui small inverted choice checkbox" @click="onSelectEstimator(null)">
                    <input class="hidden" type="checkbox" v-model="estimator" true-value="null" />
                    <label>{{ $t(`${resource}.enter`, { currency }) }}</label>
                </div>
            </div>
            <fragment v-for="(variables, key) in estimators" :key="key">
                <div class="ui very transparent white divider"></div>
                <div class="field estimator-field">
                    <div class="ui small inverted choice checkbox" @click="onSelectEstimator(key)" :class="{checked: estimator === key}">
                        <input class="hidden" type="checkbox" v-model="estimator" :true-value="key" />
                        <label>{{ $t(`estimators.${key.replace(/.*\.(\w+)$/g, '$1')}.label`) }}</label>
                    </div>
                    <validation-observer :ref="`validator-${key}`" class="collapsable grouped fields" tag="div" v-slot="{invalid}">
                        <div v-for="variable of variables" :key="variable.name" class="field">
                            <label>{{ $t(`scopes.${variable.scope.type}.variables.${variable.name}.label`, { currency }) }}</label>
                            <app-variable :model="variable" :rules="{required: true}" variant="inverted fluid transparent selection" :currency="currency" />
                        </div>
                        <div class="field">
                            <div class="ui right aligned vertical compressed segment">
                                <button class="ui mini button" :disabled="invalid" ref="button"
                                    @click.prevent="onEstimateVariable">{{ $t(`actions.estimate.label`) }}</button>
                            </div>
                        </div>
                    </validation-observer>
                </div>
            </fragment>
        </div>
    </fragment>
</template>

<script>
import AppVariable from './AppVariable.vue'

import { ValidationObserver } from 'vee-validate'

export default {
    components: {
        AppVariable,
        ValidationObserver
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        currency: {
            type: String,
        },
    },
    data() {
        return {
            menu: {
                active: false
            }
        }
    },
    computed: {
        isCalculated() {
            return this.value && this.value.function != null
        },
        isEstimable() {
            return this.model.schema.type === 'object' && this.model.schema.properties?.function
        },
        resource() {
            return `scopes.${this.model.scope.type}.variables.${this.model.name}`
        },
        estimator() {
            return this.value && this.value.function
        },
        estimators() {
            const estimators = this.model.schema['x-variable'].estimators

            return Object.keys(estimators).reduce((result, key) => {
                result[key] = Object.keys(estimators[key]).map(name => {
                    const reference = estimators[key][name].$$ref
                    return {
                        ...this.$store.getters.variable(reference, this.model.scope),
                        scope: this.$store.getters.scope(reference, this.model.scope).scope
                    }
                }).filter(
                    variable => !('x-variable' in variable.schema 
                        && variable.schema['x-variable'].category === 'parameter' 
                        && variable.context[variable.name] === null)
                ).sort((a, b) => 
                    this.$config.get(`scopes.${this.model.scope.type}.variables.${a.name}.position`, 0)
                        - this.$config.get(`scopes.${this.model.scope.type}.variables.${b.name}.position`, 0)
                )

                return result
            }, {})
        },
        value() {
            return this.model.context[this.model.name]
        }
    },
    methods: {
        onEstimateVariable(event) {
            if (this.$refs[`validator-${this.estimator}`][0].flags.dirty) {
                this.$queue.enqueue(async () => {
                    await this.$store.dispatch('refresh', {
                        scope: this.model.scope
                    })

                    this.$refs[`validator-${this.estimator}`][0].reset()

                    this.onToggleMenu(event)
                })
            } else {
                this.onToggleMenu(event)
            }

            this.$refs.menu.blur(event)
        },
        onFocusVariable(event) {
            if (this.value && this.value.function != null && event.relatedTarget !== this.$refs.menu) {
                this.onToggleMenu(event)
            }
        },
        onInputVariable(event) {
            if (this.value && this.value.function != null) {
                this.onToggleMenu(event)
            }
        },
        onSelectEstimator(key) {
            if (this.value.function !== key) {
                this.value.function = key

                if (key !== null) {
                    this.value.value = null
                } 
                else if (
                    key == "de.bluekit.simulation.computation.ShaftHeightEstimator"
                    && !this.model.engineRoom) {
                    this.model.engineRoom = false
                }

                this.$queue.enqueue(async () => {
                    await this.$store.dispatch('patch', {
                        scope: this.model.scope,
                        attributes: [this.model.name]
                    })
                })

                if (key !== null && this.$refs[`validator-${key}`][0].validate({silent: true})) {
                    this.$queue.enqueue(async () => {
                        await this.$store.dispatch('refresh', {
                            scope: this.model.scope
                        })
                    })
                } else {
                    this.value.value = null
                }
            }

            if (key === null) {
               // this.$nextTick(() => this.$refs.input.focus())
            }
        },
        onToggleMenu(event) {
            if (event && event.type === 'focusout' && (
                    (event.relatedTarget !== null && this.$refs.menu.contains(event.relatedTarget))
                    || event.relatedTarget === this.$refs.toggle
                    || !this.menu.active
                )) {
                return
            }

            this.menu.active = !this.menu.active
            if (this.menu.active) {
                this.$nextTick(() => this.$refs.menu.focus(event))
            } else {
                this.$nextTick(() => this.$refs.toggle.blur(event))
            }
        },
        focus() {
            this.onFocusVariable({})
        }
    }
}
</script>

<style>
    .ui.hidden.segment {
        display: none !important;
    }
</style>